"use client";

import React from 'react';
import { buildClientUrl } from '@/lib/api';
import { getApiToken as getApiTokenAsync } from '@/lib/token';
import ImageGalleryCard from '@/components/ImageGalleryCard';
import { useInView } from '@/hooks/useInView';
import type { GalleryListItemDto } from '@/lib/schemas/media';

type Props = {
  initial: GalleryListItemDto[];
  sort: string;
  window?: string;
};

export default function GalleryInfinite({ initial, sort, window }: Props) {
  const [items, setItems] = React.useState<GalleryListItemDto[]>(initial || []);
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const { ref, inView } = useInView<HTMLDivElement>({ rootMargin: '600px' });

  const fetchNext = React.useCallback(async () => {
    if (loading || done) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '4');
      params.set('skip', String(items.length));
      if (sort && sort !== 'top') params.set('sort', sort);
      if (window && window !== 'all') params.set('window', window);
      const hdrs: Record<string, string> = {};
      try { const tk = await getApiTokenAsync(); if (tk) hdrs['Authorization'] = `Bearer ${tk}`; } catch {}
      const res = await fetch(buildClientUrl(`/api/gallery?${params.toString()}`), { cache: 'no-store', headers: hdrs });
      if (!res.ok) { setDone(true); return; }
      const arr: any[] = await res.json();
      const next: GalleryListItemDto[] = (Array.isArray(arr) ? arr : [])
        .filter((g: any) => {
          const n = String(g?.name || '').toLowerCase();
          return !(n.includes('chat') && n.includes('library'));
        });
      if (next.length === 0) { setDone(true); return; }
      setItems(prev => {
        const seen = new Set(prev.map(g => g.galleryId));
        const dedup = next.filter(g => !seen.has(g.galleryId));
        return prev.concat(dedup);
      });
    } catch {
      setDone(true);
    } finally {
      setLoading(false);
    }
  }, [loading, done, items.length, sort, window]);

  React.useEffect(() => {
    if (inView) fetchNext();
  }, [inView, fetchNext]);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((g) => (
          <ImageGalleryCard
            key={g.galleryId}
            galleryId={g.galleryId}
            name={g.name ?? undefined}
            description={g.description ?? undefined}
            images={Array.isArray(g.images) ? g.images : []}
            updatedAt={(g as any).dateUpdated ?? (g as any).DateUpdated ?? undefined}
            unsafe={Boolean((g as any).isUnsafeContent ?? (g as any).IsUnsafeContent) || (Array.isArray(g.images) && g.images.some((img: any) => img.isUnsafeContent || img.IsUnsafeContent || img.unsafe || img.Unsafe))}
            audience={g.audience}
          />
        ))}
      </div>
      {!done && (
        <div ref={ref} className="h-10" aria-hidden />
      )}
    </>
  );
}
